import type {Component, ComponentDefinition, Editor} from 'grapesjs';
import {getAssetUrl} from '@Utils/s3.util';
import {hexToRgba, PMW_COLORS_NEUTRAL, rgbaToRgbaString} from '@Utils/color.util';
import {assertNever} from '@Utils/assert-never.util';
import {
  COMPONENT_LOADING_OVERLAY_CLASS,
  COMPONENT_LOADING_OVERLAY_WRAPPER_ID,
  EMAIL_MAKER_COMPONENT_TYPES,
  EMAIL_MAKER_PROPS,
  EMAIL_MEDIA_OVERLAY_ID,
  EMAIL_OVERLAY_ID,
  MOBILE_CSS_PROP_PREFIX,
  TABLET_CSS_PROP_PREFIX,
} from '@EmailMakerReact/email-maker-react.constants';
import {EmailMakerCanvasDevice} from '@EmailMakerReact/email-maker-react.types';
import type {ComponentLoadingOverlayStyle} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {EmailMediaOverlayVariant, EmailOverlayStyle} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {getMainWrapper, getMediaComponentFromElement, getStringProp, isMediaComponentLoading} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {getReadOnlyFooter} from '@EmailMakerReact/lib/email-maker-html-export-library';
import {getCurrentZoom} from '@EmailMakerReact/lib/email-maker-zoom-library';
import {isVideoLinkWrapper} from '@EmailMakerReact/lib/email-maker-anchor-link-library';

/**
 * Canvas overlay handling for the React email maker.
 *
 * Overlay elements are DOM-level (never components, except the full-canvas overlay) and are
 * created/removed with the UndoManager stopped, so they never reach the save payload.
 */

/** Same value as the EMAIL_MAKER_PROPS.MEDIA_TYPE prop, used as a CSS class on media elements. */
const MEDIA_TYPE_CLASS = EMAIL_MAKER_PROPS.MEDIA_TYPE;

const MEDIA_OVERLAY_ICON_DISPLAY_THRESHOLD = 125;
const MEDIA_OVERLAY_TEXT_DISPLAY_THRESHOLD = 75;
const MEDIA_OVERLAY_SCRIM_ALPHA = 0.5;
const MEDIA_OVERLAY_SCRIM_COLOR = rgbaToRgbaString(hexToRgba(PMW_COLORS_NEUTRAL.NEUTRAL_90, MEDIA_OVERLAY_SCRIM_ALPHA));
const MEDIA_OVERLAY_BACKDROP_BLUR = 'blur(10px)';
const MEDIA_OVERLAY_ICON_SIZE_PX = 40;
const MEDIA_OVERLAY_Z_INDEX = 1;
const MEDIA_OVERLAY_TEXT_SIZE_PX = 14;
const MEDIA_OVERLAY_TEXT_WEIGHT = 400;
const MEDIA_OVERLAY_DROP_ILLUSTRATION_WIDTH_PX = 118;
const MEDIA_OVERLAY_HOVER_ICON_ASSET = 'email-maker-media-component-hover.png?t=1';
const MEDIA_OVERLAY_DROP_ILLUSTRATION_ASSET = 'ai-chat-drag-drop-overlay-image.webp';
const MEDIA_OVERLAY_VARIANT_ATTRIBUTE = 'data-overlay-variant';
const WRAPPED_MEDIA_ELEMENT_SELECTOR = ':scope > :last-child';
const MEDIA_OVERLAY_CONTENT_SELECTOR = ':scope > :first-child';
export const IMAGE_OVERLAY_DROP_TEXT_KEY = 'pmwjs_email_image_overlay_drop_text';

const EMAIL_OVERLAY_CLASSES_MAP: Record<EmailOverlayStyle, string> = {
  [EmailOverlayStyle.LOADING]: '-loading',
  [EmailOverlayStyle.AI_EDIT]: '-ai-edit',
};

interface MediaOverlayGeometry {
  left: number;
  width: number;
}

interface MediaOverlayHint {
  visual: HTMLImageElement;
  text: string;
}

interface OverlayVisualSize {
  width: string;
  height: string;
}

/** i18n keys for the media hover overlay: `add` for the empty placeholder, `replace` once the user has set their own asset. */
interface MediaOverlayTextKeys {
  add: string;
  replace: string;
}

const IMAGE_OVERLAY_TEXT_KEYS: MediaOverlayTextKeys = {
  add: 'pmwjs_email_image_overlay_text',
  replace: 'pmwjs_email_image_overlay_replace_text',
};

const MEDIA_OVERLAY_TEXT_KEYS_BY_TYPE: Record<string, MediaOverlayTextKeys> = {
  [EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE]: IMAGE_OVERLAY_TEXT_KEYS,
  [EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_VIDEO]: {
    add: 'pmwjs_email_video_overlay_text',
    replace: 'pmwjs_email_video_overlay_replace_text',
  },
  [EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_STICKER]: {
    add: 'pmwjs_email_sticker_overlay_text',
    replace: 'pmwjs_email_sticker_overlay_replace_text',
  },
  [EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_ICON]: {
    add: 'pmwjs_email_icon_overlay_text',
    replace: 'pmwjs_email_icon_overlay_replace_text',
  },
};

/** Returns the full-canvas loading/AI overlay component, when present. */
export function getLoadingOverlayForEmail(editor: Editor): Component | null {
  const overlay = editor.DomComponents.getWrapper()?.find(`#${EMAIL_OVERLAY_ID}`);

  if (overlay && overlay.length > 0) {
    return overlay[0];
  }

  return null;
}

/** Resizes the overlay to cover the wrapper plus footer. */
export function fixLoadingOverlayForEmailSize(editor: Editor): void {
  const overlay = getLoadingOverlayForEmail(editor);
  const wrapper = getMainWrapper(editor);
  const footer = getReadOnlyFooter(editor);

  if (!overlay || !wrapper || !footer) {
    return;
  }

  const wrapperHeight = wrapper.getEl()?.offsetHeight;
  const footerHeight = footer.getEl()?.offsetHeight;

  const height = wrapperHeight && footerHeight ? `${(wrapperHeight + footerHeight).toString()}px` : '100%';

  overlay.setStyle({height});
}

/** Shows/hides the full-canvas loading or AI-edit overlay, outside the undo history. */
export function toggleLoadingOverlayForEmail(editor: Editor, show: boolean, style: EmailOverlayStyle = EmailOverlayStyle.LOADING): void {
  const overlay = getLoadingOverlayForEmail(editor);
  if (show && overlay) {
    return;
  }

  if (!show && !overlay) {
    return;
  }

  editor.UndoManager.stop();

  if (show) {
    editor.addComponents(buildEmailOverlayDefinition(style), {at: 0});
    fixLoadingOverlayForEmailSize(editor);
  } else if (overlay) {
    editor.getComponents().remove(overlay);
  }

  editor.UndoManager.start();
}

export function showMediaComponentOverlay(editor: Editor, element: HTMLElement | undefined, variant: EmailMediaOverlayVariant = EmailMediaOverlayVariant.HOVER): void {
  if (!element) {
    return;
  }

  const mediaComponent = getMediaComponentFromElement(editor, element);
  if (isMediaComponentLoading(mediaComponent)) {
    return;
  }

  editor.UndoManager.stop();

  const wrapper = getMediaOverlayWrapper(element) ?? wrapMediaElementForOverlay(element);

  if (wrapper.getAttribute(MEDIA_OVERLAY_VARIANT_ATTRIBUTE) !== variant) {
    removeMediaOverlayContent(wrapper);
    const hint = getMediaOverlayHint(mediaComponent, element, variant);
    wrapper.insertBefore(getMediaComponentOverlayContent(element, hint, getMediaOverlayGeometry(editor, element, wrapper)), element);
    wrapper.setAttribute(MEDIA_OVERLAY_VARIANT_ATTRIBUTE, variant);
  }

  editor.UndoManager.start();
}

export function hideAllMediaComponentOverlays(editor: Editor): void {
  const canvasDocument: Document | undefined = editor.Canvas.getDocument();
  const wrappers = canvasDocument?.querySelectorAll<HTMLElement>(`#${EMAIL_MEDIA_OVERLAY_ID}`) ?? [];
  for (const wrapper of wrappers) {
    hideMediaComponentOverlay(editor, getWrappedMediaElement(wrapper));
  }
}

export function hideMediaComponentOverlay(editor: Editor, element: HTMLElement | undefined): void {
  if (!element) {
    return;
  }

  const overlayWrapper = getMediaOverlayWrapper(element);
  if (!overlayWrapper?.parentNode) {
    return;
  }

  editor.UndoManager.stop();
  overlayWrapper.parentNode.insertBefore(element, overlayWrapper);
  overlayWrapper.parentNode.removeChild(overlayWrapper);
  editor.UndoManager.start();
}

export function prepareMediaComponentOverlay(editor: Editor, element: HTMLElement | undefined): void {
  if (!element || getMediaOverlayWrapper(element)) {
    return;
  }

  editor.UndoManager.stop();
  wrapMediaElementForOverlay(element);
  editor.UndoManager.start();
}

export function clearMediaComponentOverlayContent(editor: Editor, element: HTMLElement | undefined): void {
  const wrapper = element ? getMediaOverlayWrapper(element) : null;
  if (!element || !wrapper) {
    return;
  }

  editor.UndoManager.stop();
  removeMediaOverlayContent(wrapper);
  wrapper.removeAttribute(MEDIA_OVERLAY_VARIANT_ATTRIBUTE);
  editor.UndoManager.start();
}

function getMediaOverlayWrapper(element: HTMLElement): HTMLElement | null {
  const elementParent = element.parentElement;
  return elementParent?.id === EMAIL_MEDIA_OVERLAY_ID ? elementParent : null;
}

function wrapMediaElementForOverlay(element: HTMLElement): HTMLDivElement {
  const parentAlignment = element.parentElement?.getAttribute('align');
  const wrapper = document.createElement('div');
  wrapper.id = EMAIL_MEDIA_OVERLAY_ID;
  wrapper.style.position = 'relative';
  if (parentAlignment) {
    wrapper.setAttribute('align', parentAlignment);
  }

  element.parentNode?.insertBefore(wrapper, element);
  wrapper.appendChild(element);

  return wrapper;
}

function removeMediaOverlayContent(wrapper: HTMLElement): void {
  getMediaOverlayContentElement(wrapper)?.remove();
}

function getWrappedMediaElement(wrapper: HTMLElement): HTMLElement | undefined {
  return wrapper.querySelector<HTMLElement>(WRAPPED_MEDIA_ELEMENT_SELECTOR) ?? undefined;
}

function getMediaOverlayContentElement(wrapper: HTMLElement): HTMLElement | null {
  return wrapper.children.length > 1 ? wrapper.querySelector<HTMLElement>(MEDIA_OVERLAY_CONTENT_SELECTOR) : null;
}

/** Shows/hides a per-component DOM loading overlay (special-cased for table cells). */
export function toggleComponentLoadingOverlay(editor: Editor, component: Component, show: boolean): void {
  const componentEl = component.getEl();
  if (!componentEl) {
    return;
  }

  const overlayId = getLoadingOverlayIdFromComponent(component);
  const isTableCell = componentEl.tagName === 'TD';

  if (show) {
    const overlayEl = createLoadingOverlayElement(overlayId);

    if (isTableCell) {
      showLoadingOverlayForTableCell(componentEl, overlayEl);
    } else {
      showLoadingOverlayForGeneralCase(editor, componentEl, overlayEl);
    }
  } else if (isTableCell) {
    hideLoadingOverlayForTableCell(componentEl, overlayId);
  } else {
    hideLoadingOverlayForGeneralCase(componentEl, overlayId);
  }
}

/** Clears overlays left stale by a previous session after load. */
export function removeComponentLoadingOverlaysOnLoad(editor: Editor): void {
  getAllComponentLoadingOverlays(editor).forEach((loadingOverlay) => {
    const componentId = getComponentIdFromLoadingOverlayElement(loadingOverlay);
    const component = editor.DomComponents.getWrapper()?.find(`#${componentId}`)[0];

    if (!component) {
      return;
    }

    toggleComponentLoadingOverlay(editor, component, false);
  });
}

/** Re-measures every loading overlay; run after device switches and window resizes. */
export function fixComponentLoadingOverlaysOnWindowResize(editor: Editor): void {
  getAllComponentLoadingOverlays(editor).forEach((loadingOverlay) => {
    const componentId = getComponentIdFromLoadingOverlayElement(loadingOverlay);
    const componentEl = editor.Canvas.getBody().querySelector<HTMLElement>(`#${componentId}`);

    if (!componentEl) {
      return;
    }

    Object.assign(loadingOverlay.style, getStylesForComponentLoadingOverlay(editor, componentEl));
  });
}

function buildEmailOverlayDefinition(style: EmailOverlayStyle): ComponentDefinition {
  return {
    attributes: {
      id: EMAIL_OVERLAY_ID,
      'data-is-email-overlay': true,
    },
    classes: EMAIL_OVERLAY_CLASSES_MAP[style],
    selectable: false,
    hoverable: false,
  };
}

function getAllComponentLoadingOverlays(editor: Editor): NodeListOf<HTMLElement> {
  return editor.Canvas.getBody().querySelectorAll<HTMLElement>(`.${COMPONENT_LOADING_OVERLAY_CLASS}`);
}

function getLoadingOverlayIdFromComponent(component: Component): string {
  return `${component.ccid}-${COMPONENT_LOADING_OVERLAY_CLASS}`;
}

/** Returns the component id encoded in a loading overlay element id. */
function getComponentIdFromLoadingOverlayElement(loadingOverlayEl: HTMLElement): string {
  return loadingOverlayEl.id.split('-')[0];
}

/** Returns styles for a component loading overlay from the component's element. */
function getStylesForComponentLoadingOverlay(editor: Editor, componentEl: HTMLElement): ComponentLoadingOverlayStyle {
  return {
    width: `${componentEl.clientWidth}px`,
    marginLeft: calculateMediaOverlayLeftMargin(editor, componentEl),
  };
}

function createLoadingOverlayElement(overlayId: string): HTMLDivElement {
  const overlayEl = document.createElement('div');
  overlayEl.id = overlayId;
  overlayEl.classList.add(COMPONENT_LOADING_OVERLAY_CLASS);
  overlayEl.setAttribute('draggable', 'false');
  overlayEl.setAttribute('selectable', 'false');
  overlayEl.setAttribute('hoverable', 'false');
  overlayEl.setAttribute('droppable', 'false');

  return overlayEl;
}

function showLoadingOverlayForTableCell(componentEl: HTMLElement, overlayEl: HTMLDivElement): void {
  componentEl.style.position = 'relative';
  Object.assign(overlayEl.style, {
    position: 'absolute',
    inset: '0',
  });

  componentEl.insertBefore(overlayEl, componentEl.firstChild);
}

function showLoadingOverlayForGeneralCase(editor: Editor, componentEl: HTMLElement, overlayEl: HTMLDivElement): void {
  const parentAlignment = componentEl.parentElement?.getAttribute('align') ?? null;

  const wrapper = document.createElement('div');
  wrapper.id = COMPONENT_LOADING_OVERLAY_WRAPPER_ID;

  componentEl.parentNode?.insertBefore(wrapper, componentEl);
  wrapper.appendChild(overlayEl);
  wrapper.appendChild(componentEl);
  if (parentAlignment) {
    wrapper.setAttribute('align', parentAlignment);
  }

  wrapper.style.position = 'relative';
  Object.assign(overlayEl.style, {
    position: 'absolute',
    inset: '0',
    'border-radius': componentEl.style.borderRadius,
  });

  Object.assign(overlayEl.style, getStylesForComponentLoadingOverlay(editor, componentEl));
}

function hideLoadingOverlayForTableCell(componentEl: HTMLElement, loadingOverlayId: string): void {
  const overlay = componentEl.querySelector(`#${loadingOverlayId}`);
  if (overlay) {
    overlay.remove();
    componentEl.style.position = '';
  }
}

function hideLoadingOverlayForGeneralCase(componentEl: HTMLElement, loadingOverlayId: string): void {
  const overlayWrapper = componentEl.parentNode as HTMLElement | null;
  const loadingOverlay = overlayWrapper?.firstChild as HTMLElement | null;
  if (overlayWrapper?.id === COMPONENT_LOADING_OVERLAY_WRAPPER_ID && loadingOverlay?.id === loadingOverlayId) {
    overlayWrapper.parentNode?.insertBefore(componentEl, overlayWrapper);
    overlayWrapper.parentNode?.removeChild(overlayWrapper);
  }
}

function calculateElementOverlayLeftMargin(element: HTMLElement): string {
  return window.getComputedStyle(element).marginLeft;
}

/** Derives the overlay's left margin from the element's percentage width, parent alignment, and current zoom. */
function calculateMediaOverlayLeftMargin(editor: Editor, element: HTMLElement): string {
  const zoomValue = getCurrentZoom(editor);
  const parentAlignment = element.parentElement?.getAttribute('align') ?? '';
  const width = getElementResponsiveWidth(editor, element);
  const percentageWidth = parseFloat(width);

  let marginLeft = calculateElementOverlayLeftMargin(element);

  if (isNaN(percentageWidth) || percentageWidth <= 0) {
    return `${parseInt(marginLeft, 10) * (1 / zoomValue)}px`;
  }

  const elementRect = element.getBoundingClientRect();

  switch (parentAlignment) {
    case 'center':
    case 'middle':
      marginLeft = `${elementRect.width * (50 / percentageWidth - 0.5)}px`;
      break;
    case 'right':
      marginLeft = `${elementRect.width * (100 / percentageWidth - 1)}px`;
      break;
    default:
      marginLeft = calculateElementOverlayLeftMargin(element);
      break;
  }

  return `${parseInt(marginLeft, 10) * (1 / zoomValue)}px`;
}

/** Returns the element's width for the selected device, read from inline device-prefixed props with media-type-rule and inline-style fallbacks. */
function getElementResponsiveWidth(editor: Editor, element: HTMLElement): string {
  const STYLE_SEPARATOR = ';';
  const STYLE_KEY_VALUE_SEPARATOR = ':';

  const deviceStylePrefix: Record<string, string> = {
    [EmailMakerCanvasDevice.DESKTOP]: '',
    [EmailMakerCanvasDevice.MOBILE]: MOBILE_CSS_PROP_PREFIX,
    [EmailMakerCanvasDevice.TABLET]: TABLET_CSS_PROP_PREFIX,
  };

  const selectedDevice = editor.Devices.getSelected()?.id;

  if (!selectedDevice) {
    return element.style.width;
  }

  let width: string | null = null;

  element.style.cssText.split(STYLE_SEPARATOR).forEach((style) => {
    const parts = style.split(STYLE_KEY_VALUE_SEPARATOR);
    if (parts.length === 2 && parts[0].trim() === `${deviceStylePrefix[selectedDevice]}width`) {
      width = parts[1];
    }
  });

  if (!width) {
    const mediaTypeRules = editor.Css.getRules(`.${MEDIA_TYPE_CLASS}`);
    const ruleWidth = element.classList.contains(MEDIA_TYPE_CLASS) && mediaTypeRules.length > 0 ? mediaTypeRules[0].getStyle().width : undefined;
    width = typeof ruleWidth === 'string' ? ruleWidth : element.style.width;
  }

  return width.replace(/!important/g, '').trim();
}

/** True once the media component shows a user-provided asset (uploaded/stock/embedded) rather than its initial placeholder. */
export function doesMediaComponentHaveUserAsset(component: Component | undefined): boolean {
  if (!component) {
    return false;
  }

  const hasHashedFilename = Boolean(getStringProp(component, EMAIL_MAKER_PROPS.HASHED_FILENAME));

  return hasHashedFilename || isVideoLinkWrapper(component);
}

/** Picks the "add" vs "replace" overlay i18n key for the media type. */
function getMediaOverlayTextKey(pmwType: string, hasUserAsset: boolean): string {
  const keys = MEDIA_OVERLAY_TEXT_KEYS_BY_TYPE[pmwType] ?? IMAGE_OVERLAY_TEXT_KEYS;

  return hasUserAsset ? keys.replace : keys.add;
}

/** Sub-pixel, unzoomed; offsetLeft/offsetWidth round and leave a sliver. */
function getMediaOverlayGeometry(editor: Editor, mediaImage: HTMLElement, wrapper: HTMLElement): MediaOverlayGeometry {
  const zoomValue = getCurrentZoom(editor);
  const mediaRect = mediaImage.getBoundingClientRect();
  const wrapperRect = wrapper.getBoundingClientRect();

  return {
    left: (mediaRect.left - wrapperRect.left) / zoomValue,
    width: mediaRect.width / zoomValue,
  };
}

function getMediaOverlayHint(mediaComponent: Component | undefined, mediaImage: HTMLElement, variant: EmailMediaOverlayVariant): MediaOverlayHint {
  switch (variant) {
    case EmailMediaOverlayVariant.HOVER:
      return getHoverOverlayHint(mediaComponent, mediaImage);
    case EmailMediaOverlayVariant.DROP:
      return getDropOverlayHint();
    default:
      return assertNever(variant);
  }
}

function getHoverOverlayHint(mediaComponent: Component | undefined, mediaImage: HTMLElement): MediaOverlayHint {
  const hasUserAsset = doesMediaComponentHaveUserAsset(mediaComponent);
  const pmwType = mediaImage.getAttribute('data-pmw-type') ?? '';
  const visual = createOverlayVisual(MEDIA_OVERLAY_HOVER_ICON_ASSET, {width: `${MEDIA_OVERLAY_ICON_SIZE_PX}px`, height: `${MEDIA_OVERLAY_ICON_SIZE_PX}px`});

  return {visual, text: window.i18next.t(getMediaOverlayTextKey(pmwType, hasUserAsset))};
}

function getDropOverlayHint(): MediaOverlayHint {
  const visual = createOverlayVisual(MEDIA_OVERLAY_DROP_ILLUSTRATION_ASSET, {width: `${MEDIA_OVERLAY_DROP_ILLUSTRATION_WIDTH_PX}px`, height: 'auto'});

  return {visual, text: window.i18next.t(IMAGE_OVERLAY_DROP_TEXT_KEY)};
}

function createOverlayVisual(assetFilename: string, size: OverlayVisualSize): HTMLImageElement {
  const visual = document.createElement('img');
  visual.src = getAssetUrl(assetFilename);
  visual.alt = '';
  Object.assign(visual.style, size);

  return visual;
}

function getMediaComponentOverlayContent(mediaImage: HTMLElement, hint: MediaOverlayHint, geometry: MediaOverlayGeometry): HTMLElement {
  const overlayDiv = document.createElement('div');

  Object.assign(overlayDiv.style, {
    position: 'absolute',
    left: `${geometry.left}px`,
    top: '0',
    width: `${geometry.width}px`,
    height: '100%',
    zIndex: MEDIA_OVERLAY_Z_INDEX,
    backgroundColor: MEDIA_OVERLAY_SCRIM_COLOR,
    backdropFilter: MEDIA_OVERLAY_BACKDROP_BLUR,
    WebkitBackdropFilter: MEDIA_OVERLAY_BACKDROP_BLUR,
    pointerEvents: 'none',
    borderRadius: mediaImage.style.borderRadius,
    overflow: 'hidden',
  });

  const mediaRect = mediaImage.getBoundingClientRect();
  const showIcon = mediaRect.height > MEDIA_OVERLAY_ICON_DISPLAY_THRESHOLD && mediaRect.width > MEDIA_OVERLAY_ICON_DISPLAY_THRESHOLD;
  const showText = mediaRect.height > MEDIA_OVERLAY_TEXT_DISPLAY_THRESHOLD && mediaRect.width > MEDIA_OVERLAY_TEXT_DISPLAY_THRESHOLD;

  const contentContainer = document.createElement('div');
  const text = document.createElement('p');
  text.innerText = hint.text;

  Object.assign(contentContainer.style, {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    color: 'white',
    width: '100%',
  });

  Object.assign(text.style, {
    padding: '0',
    margin: '0',
    color: '#FFF',
    'text-align': 'center',
    'font-family': '"Figtree", Helvetica, Arial, sans-serif',
    'font-size': `${MEDIA_OVERLAY_TEXT_SIZE_PX}px`,
    'font-style': 'normal',
    'font-weight': MEDIA_OVERLAY_TEXT_WEIGHT,
    'line-height': '150%',
  });

  if (showIcon) {
    contentContainer.appendChild(hint.visual);
  }
  if (showText) {
    contentContainer.appendChild(text);
  }

  overlayDiv.appendChild(contentContainer);

  return overlayDiv;
}
